import React from "react";

import {
  type ReadinessBlockerEntry,
  ReadinessBlockerList,
} from "@/components/shared/ReadinessBlockerList";
import { Alert, Badge, Heading, Icon, LinkButton, Text } from "@/components/ui";
import { blockersForGate } from "@/server/readiness-ladder";
import type { ReadinessBlocker, ReadinessBlockerTarget, ReadinessState } from "@/server/types";
import { lockedReason } from "@/state/step-locks";
import { isIdentityComplete } from "@/state/store";

import { PROGRAMME_SECTION_LABELS } from "./governance-stations";
import type { useProgrammeGovernance } from "./useProgrammeGovernance";

export interface GovernanceRequirementsProps {
  governance: NonNullable<ReturnType<typeof useProgrammeGovernance>["governance"]>;
  navigationItems: ReturnType<typeof useProgrammeGovernance>["navigationItems"];
  /** The editor address a blocker is cleared at, or null when it has none. */
  hrefFor: (target: ReadinessBlockerTarget) => string | null;
  /** The programme's workflow state, which decides what closes this stage. */
  workflowState: string;
}

/**
 * The readiness the action closing each stage needs.
 *
 * A review stage counts what approval needs, the same blockers the reviewer
 * attests to when marking it reviewed. Approved closes by recording the
 * sign-offs; only export needs the top rung.
 */
function stageGate(workflowState: string): ReadinessState {
  switch (workflowState) {
    case "Awaiting Internal Review":
    case "Internal Review":
    case "Awaiting QA Approval":
    case "Approved":
      return "Ready for approval";
    case "Final Descriptor Signed Off":
    case "Exported and Locked":
      return "Ready for final export";
    default:
      return "Ready for internal review";
  }
}

/** The blockers that close this stage, which every count on the panel shows. */
export function stageBlockers<T extends Pick<ReadinessBlocker, "gates">>(
  blockers: readonly T[],
  workflowState: string,
): T[] {
  return blockersForGate(blockers, stageGate(workflowState));
}

/** What stands open at this stage: its blockers and the review findings. */
export function openRequirementCount(
  governance: Pick<GovernanceRequirementsProps["governance"], "readiness" | "blockingFindings">,
  workflowState: string,
): number {
  return (
    stageBlockers(governance.readiness.blockers, workflowState).length +
    governance.blockingFindings.length
  );
}

/**
 * Blockers as list rows, each with the way to it: what can be done now first,
 * then in the editor's own order.
 */
export function readinessBlockerEntries(
  list: readonly ReadinessBlocker[],
  navigationItems: GovernanceRequirementsProps["navigationItems"],
  hrefFor: GovernanceRequirementsProps["hrefFor"],
): ReadinessBlockerEntry[] {
  // The list led with a locked row and put Identity below the guidance naming it first.
  const rank = (blocker: ReadinessBlocker): number => {
    const index = navigationItems.findIndex((item) => item.key === blocker.target?.section);
    if (index >= 0 && navigationItems[index]?.locked) {
      return navigationItems.length + index + 1;
    }
    return index >= 0 ? index : navigationItems.length;
  };
  // Checked once for every locked row rather than per row, as the tab strip does.
  let identityComplete: boolean | undefined;
  const identityOnce = (): boolean => {
    identityComplete ??= isIdentityComplete();
    return identityComplete;
  };
  return [...list]
    .sort((left, right) => rank(left) - rank(right))
    .map((blocker) => {
      const target = blocker.target;
      const destination = target
        ? navigationItems.find((item) => item.key === target.section)
        : undefined;
      const section = target
        ? (PROGRAMME_SECTION_LABELS[target.section] ?? target.section)
        : undefined;
      const href = target && destination && !destination.locked ? hrefFor(target) : null;
      return {
        key: `${blocker.requirement}-${blocker.message}`,
        id: blocker.requirement,
        message: blocker.message,
        section,
        sectionKey: target?.section,
        href: href ?? undefined,
        lockedReason:
          target && destination?.locked
            ? `${lockedReason(target.section, identityOnce())}.`
            : undefined,
      };
    });
}

/** What the open items stop at this stage, and who may proceed past them. */
function requirementsGuidance(workflowState: string): string {
  switch (workflowState) {
    case "Awaiting Internal Review":
    case "Internal Review":
      return "Approval needs these items. A reviewer can still mark the programme reviewed with them open, by attesting to them in the review comment.";
    case "Awaiting QA Approval":
      return "Approval needs every item complete, or a Client Admin override recorded with a reason.";
    default:
      return "Complete every item before taking an action that closes this stage.";
  }
}

export function GovernanceRequirements({
  governance,
  navigationItems,
  hrefFor,
  workflowState,
}: GovernanceRequirementsProps): React.ReactElement {
  const submittedHref = hrefFor({ section: "snapshot" });
  const blockers = governance.readiness.blockers;
  const now = stageBlockers(blockers, workflowState);
  const later = blockers.filter((blocker) => !now.includes(blocker));
  const singles = (list: readonly ReadinessBlocker[]): ReadinessBlockerEntry[] =>
    readinessBlockerEntries(list, navigationItems, hrefFor);
  // One row and one action per section: ten blockers cleared in the same
  // place read as ten copies of one button.
  const entriesFor = (list: readonly ReadinessBlocker[]): ReadinessBlockerEntry[] => {
    const rows = singles(list);
    const detail = (row: ReadinessBlockerEntry): ReadinessBlockerEntry => ({
      ...row,
      href: undefined,
      lockedReason: undefined,
    });
    return rows.reduce<ReadinessBlockerEntry[]>((grouped, row) => {
      const gathered = row.sectionKey
        ? rows.filter((other) => other.sectionKey === row.sectionKey)
        : [];
      if (gathered.length < 2) {
        return [...grouped, row];
      }
      if (gathered[0] !== row) {
        return grouped;
      }
      return [
        ...grouped,
        {
          ...row,
          key: `section-${row.sectionKey}`,
          id: `section-${row.sectionKey}`,
          message: `${gathered.length} requirements to complete`,
          count: gathered.length,
          details: gathered.map(detail),
        },
      ];
    }, []);
  };
  return (
    <section className="border-t border-base-300 pt-5" aria-labelledby="requirements-heading">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Heading level={3} id="requirements-heading">
          Requirements
        </Heading>
        <Badge variant="outline" size="sm">
          {openRequirementCount(governance, workflowState)} open
        </Badge>
      </div>
      <Text tone="muted" className="mt-1" data-testid="governance-requirements-guidance">
        {requirementsGuidance(workflowState)}
      </Text>

      {now.length ? (
        <div className="mt-4" data-testid="governance-blockers">
          <Heading level={4}>Readiness checks ({now.length})</Heading>
          <ReadinessBlockerList
            entries={entriesFor(now)}
            headingLevel={5}
            testIdPrefix="governance-blocker"
          />
        </div>
      ) : null}

      {governance.blockingFindings.length ? (
        <div className="mt-4" data-testid="governance-blocking-findings">
          <Heading level={4}>Review findings ({governance.blockingFindings.length})</Heading>
          <ul className="mt-3 space-y-3">
            {governance.blockingFindings.map((finding) => (
              <li key={finding.id} className="space-y-2 text-md">
                <div className="flex items-start gap-2">
                  <Badge size="sm" tone="warning">
                    Finding
                  </Badge>
                  <span className="min-w-0 flex-1">
                    {finding.note?.trim() || "Changes requested by the reviewer."}
                  </span>
                </div>
                {submittedHref ? (
                  <LinkButton to={submittedHref} size="xs" variant="link" icon="arrow-square-out">
                    Open submitted programme
                  </LinkButton>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {!now.length && !governance.blockingFindings.length ? (
        <Alert tone="success" className="mt-4" data-testid="governance-requirements-complete">
          <Icon name="check-circle" aria-hidden /> All requirements for this checkpoint are
          complete.
        </Alert>
      ) : null}

      {later.length ? (
        <div className="mt-4" data-testid="governance-later-blockers">
          <Heading level={4}>Needed at a later stage ({later.length})</Heading>
          <ReadinessBlockerList
            entries={entriesFor(later)}
            headingLevel={5}
            testIdPrefix="governance-later-blocker"
          />
        </div>
      ) : null}
    </section>
  );
}
